import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEARCH_KEYS } from '../../../constant/SearchKeys';
import { useClientNamesByIds } from '../../../hooks/useClientNames';
import {
  SEARCH_DEBOUNCE_MS,
  useDebouncedUrlParam,
} from '../../../hooks/useDebouncedUrlParam';
import {
  MAX_LIST_VALUES,
  isClaimStatusValue,
  isClientIdValue,
  isProductionYearValue,
  isProgramTypeValue,
  isServiceValue,
  toTitleTypes,
} from './titleFilterOptions';

const PAGE_PARAM = 'titlesPage';

/** Re-exported for TitlesV3.test.tsx compatibility. */
export { SEARCH_DEBOUNCE_MS };

/**
 * `selected` carries whatever the guard narrowed to, so a filter's query argument
 * needs no cast. `setSelected` stays `string[]`: values arriving from a combobox
 * or a hand-edited URL are unvalidated until `normalise` has run.
 */
export type TitleFilter<T extends string = string> = {
  selected: T[];
  setSelected: (values: string[]) => void;
};

/** A filter typed into a text field rather than picked from an option list. */
export type TitleTextFilter = {
  setValue: (value: string) => void;
  value: string;
};

/**
 * Drops unknown values, dedupes, and caps the length. Deduping matters: every
 * filter has an `@ArrayMaxSize` on the DTO, so a repeated value in the URL —
 * `?titlesStatus=Draft,Draft,…` — can exceed the cap and 400 the whole page.
 */
const normalise = <T extends string>(
  values: string[],
  isValid: (value: string) => value is T,
  maxValues?: number,
) => [...new Set(values.filter(isValid))].slice(0, maxValues);

/**
 * Reads a comma-joined multi-value param and writes it back the same way.
 * Validating on the way in means a hand-edited or stale URL can't reach the API
 * as a 400. `maxValues` is only needed for client ids, the one filter whose guard
 * checks uuid shape rather than membership of a fixed list.
 */
export const useFilterParam = <T extends string>(
  searchParams: URLSearchParams,
  setFilterParam: (key: string, value: string) => void,
  param: string,
  isValid: (value: string) => value is T,
  maxValues?: number,
): TitleFilter<T> => {
  const raw = searchParams.get(param);

  const selected = useMemo(
    () => (raw ? normalise(raw.split(','), isValid, maxValues) : []),
    [raw, isValid, maxValues],
  );

  const setSelected = useCallback(
    (values: string[]) =>
      setFilterParam(param, normalise(values, isValid, maxValues).join(',')),
    [setFilterParam, param, isValid, maxValues],
  );

  return { selected, setSelected };
};

/** Stable empty selection, so masking the client filter can't loop an effect. */
const NO_SELECTION: string[] = [];

type UseTitleFiltersParams = {
  /**
   * Whether this workspace renders the Client control at all — agent workspaces
   * only. Without it a `titlesClient` param has nothing to display it and no
   * control to clear it from, so it is treated as unset rather than applied.
   */
  /**
   * Lets an empty ownership answer be trusted: a failed permission check also
   * returns an empty 200, so alone it cannot tell *unowned* from *invisible*.
   */
  canSeeClients?: boolean;
  isClientFilterAvailable: boolean;
  /** Scopes the ownership lookup. Without it nothing is verified, so nothing is pruned. */
  workspaceId?: string;
};

/**
 * Owns every piece of Titles L1 list state that lives in the URL: the search
 * term, the five filters and the page. Keeping it in the URL means back
 * navigation and shared links reproduce the same result set.
 */
export const useTitleFilters = ({
  canSeeClients = false,
  isClientFilterAvailable,
  workspaceId,
}: UseTitleFiltersParams) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const name = searchParams.get(SEARCH_KEYS.TITLES_SEARCH) ?? '';
  const page = Math.max(1, Number(searchParams.get(PAGE_PARAM)) || 1);

  // `setSearchParams` applies its updater to the params of the render it was
  // created in, not to a live value, so two writes in one effect flush both
  // start from the same base and the second silently drops the first. Several
  // writers here can fire in the same flush — the search and year debounces,
  // the unowned-client prune. `pendingParamsRef` carries each write forward so
  // they compose, and resets whenever a new location lands.
  const pendingParamsRef = useRef(searchParams);
  const lastSeenParamsRef = useRef(searchParams);

  if (lastSeenParamsRef.current !== searchParams) {
    lastSeenParamsRef.current = searchParams;
    pendingParamsRef.current = searchParams;
  }

  /** Applies several param changes as one navigation. `''` deletes a param. */
  const setParams = useCallback(
    (updates: Record<string, string>) => {
      const updated = new URLSearchParams(pendingParamsRef.current);

      for (const [key, value] of Object.entries(updates)) {
        if (value) updated.set(key, value);
        else updated.delete(key);
      }

      pendingParamsRef.current = updated;
      setSearchParams(updated, { replace: true });
    },
    [setSearchParams],
  );

  const setParam = useCallback(
    (key: string, value: string) => setParams({ [key]: value }),
    [setParams],
  );

  /**
   * Writes a search or filter param and drops the page in the same navigation.
   * Done at the point of change rather than in an effect keyed on the resulting
   * selection: an effect only runs after the changed filter has already rendered,
   * so the query fires once for the old page and again for page 1. Deleting the
   * param *is* page 1.
   */
  const setFilterParam = useCallback(
    (key: string, value: string) =>
      setParams({ [PAGE_PARAM]: '', [key]: value }),
    [setParams],
  );

  const setPage = useCallback(
    (next: number) => setParam(PAGE_PARAM, String(next)),
    [setParam],
  );

  const clientParam = useFilterParam(
    searchParams,
    setFilterParam,
    SEARCH_KEYS.TITLES_CLIENT,
    isClientIdValue,
    MAX_LIST_VALUES,
  );

  // A workspace without the Client control cannot show a selected client id or
  // offer a way to deselect it, so a `titlesClient` param — a shared link, or a
  // bookmark kept across a workspace switch — would narrow the list to nothing
  // with no visible cause and no way back (every write is `replace`, so Back
  // does not help either). Treat it as unset for the query, and strip it below.
  const client: TitleFilter<string> = isClientFilterAvailable
    ? clientParam
    : { selected: NO_SELECTION, setSelected: clientParam.setSelected };

  const rawClientParam = searchParams.get(SEARCH_KEYS.TITLES_CLIENT);

  useEffect(() => {
    if (isClientFilterAvailable || rawClientParam === null) return;

    setFilterParam(SEARCH_KEYS.TITLES_CLIENT, '');
  }, [isClientFilterAvailable, rawClientParam, setFilterParam]);

  const service = useFilterParam(
    searchParams,
    setFilterParam,
    SEARCH_KEYS.TITLES_SERVICE,
    isServiceValue,
  );
  const programType = useFilterParam(
    searchParams,
    setFilterParam,
    SEARCH_KEYS.TITLES_TYPE,
    isProgramTypeValue,
  );
  const claimStatus = useFilterParam(
    searchParams,
    setFilterParam,
    SEARCH_KEYS.TITLES_STATUS,
    isClaimStatusValue,
  );

  // A client id from another workspace (shared link, or a bookmark kept across a
  // workspace switch) passes the uuid guard but narrows the list to nothing, and
  // the control has no matching option to label or deselect. Drop the ids this
  // workspace doesn't own, once we know which those are.
  const { selected: selectedClients, setSelected: setSelectedClients } = client;

  // Asked per id, so the old 100-client cap cannot distort it. The query lives
  // here rather than in a prop because the ids are the ones this hook parsed.
  const { resolvedClientIds } = useClientNamesByIds(selectedClients, {
    enabled: isClientFilterAvailable,
    workspaceId,
  });

  useEffect(() => {
    // `undefined` is "not known yet", never "owns none of these".
    if (!resolvedClientIds || !selectedClients.length) return;

    // An empty answer is only believable once something proves clients are visible.
    if (resolvedClientIds.size === 0 && !canSeeClients) return;

    const kept = selectedClients.filter((id) => resolvedClientIds.has(id));

    if (kept.length !== selectedClients.length) setSelectedClients(kept);
  }, [canSeeClients, resolvedClientIds, selectedClients, setSelectedClients]);

  const setSearchParam = useCallback(
    (value: string) => setFilterParam(SEARCH_KEYS.TITLES_SEARCH, value),
    [setFilterParam],
  );

  const {
    debounced: debouncedSearch,
    input: searchInput,
    setInput: setSearchInput,
  } = useDebouncedUrlParam(name, setSearchParam);

  // Production year is free text too, so it shares the same helper. Only a whole
  // four-digit year is ever in force: a hand-edited `abc` is ignored rather than
  // reaching the DTO's `/^\d{4}$/` as a 400. An invalid intermediate — `201`
  // while `2009` is applied — is not committed, so editing a year does not widen
  // the list to the whole catalogue and reset the page halfway through the edit.
  const yearParam = searchParams.get(SEARCH_KEYS.TITLES_YEAR) ?? '';
  const year = isProductionYearValue(yearParam) ? yearParam : '';

  const setYearParam = useCallback(
    (value: string) => setFilterParam(SEARCH_KEYS.TITLES_YEAR, value),
    [setFilterParam],
  );

  const { input: productionYearInput, setInput: setProductionYearInput } =
    useDebouncedUrlParam(year, setYearParam, {
      isValid: isProductionYearValue,
    });

  /** True when any filter is applied — drives the Filters toggle's initial state. */
  const hasFilterSelection = [
    client.selected,
    service.selected,
    programType.selected,
    year ? [year] : [],
    claimStatus.selected,
  ].some((selection) => selection.length > 0);

  /** True when search or any filter narrows the list — drives the empty state. */
  const hasFilters = Boolean(name) || hasFilterSelection;

  /**
   * Filter arguments for the titles query, as selected. Empty lists are dropped
   * at the wire boundary (`toFilterArg` in `titlesApi`) rather than here, so the
   * rule lives in one place.
   */
  const queryFilters = {
    claimStatuses: claimStatus.selected,
    ownerClientIds: client.selected,
    serviceTypes: service.selected,
    titleTypes: toTitleTypes(programType.selected),
    // `''` would reach the wire as `?year=`; the param is either a whole year or
    // absent.
    year: year || undefined,
  };

  return {
    debouncedSearch,
    filters: {
      claimStatus,
      client,
      productionYear: {
        setValue: setProductionYearInput,
        value: productionYearInput,
      },
      programType,
      service,
    },
    hasFilterSelection,
    hasFilters,
    name,
    page,
    queryFilters,
    searchInput,
    setPage,
    setSearchInput,
  };
};

/** Everything the list page and the filter bar share, as one value. */
export type TitleFilters = ReturnType<typeof useTitleFilters>;
